import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  DatePicker,
  Popconfirm,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  useRegulatoryDocuments,
  useCreateRegulatoryDocument,
  useUpdateRegulatoryDocument,
  useDeleteRegulatoryDocument,
} from '@hooks/useRegulatory';
import type {
  RegulatoryDocumentItem,
  RegulatoryDocumentFilters,
  RegulatoryDocumentType,
  RegulatoryDocumentStatus,
} from '@/types/regulatory';

// Поріг «закінчується» — як у GET /regulatory/documents/expiring (days=90 за замовчуванням)
const MD_EXPIRING_SOON_DAYS = 90;

// Дати документа на сервері необов'язкові
const mdFormatDate = (mdValue: string | null): string =>
  mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—';

// timestamptz на сервері приймає лише UTC: дата без часу дала б DateTime Kind=Unspecified
const mdToUtcDate = (mdDate: Dayjs | null | undefined): string | undefined =>
  mdDate ? `${mdDate.format('YYYY-MM-DD')}T00:00:00Z` : undefined;

// Сервер не надсилає ознаку «закінчується» — рахуємо з expiryDate
const mdIsExpiringSoon = (mdRecord: RegulatoryDocumentItem): boolean => {
  if (mdRecord.isExpired || !mdRecord.expiryDate) return false;
  return dayjs(mdRecord.expiryDate).diff(dayjs(), 'day', true) <= MD_EXPIRING_SOON_DAYS;
};

const MD_DOC_TYPE_OPTIONS: { value: RegulatoryDocumentType | ''; label: string }[] = [
  { value: '', label: 'Всі типи' },
  { value: 'Certificate', label: 'Сертифікат' },
  { value: 'Declaration', label: 'Декларація' },
  { value: 'Registration', label: 'Реєстрація' },
  { value: 'License', label: 'Ліцензія' },
  { value: 'Permit', label: 'Дозвіл' },
];

const MD_DOC_TYPE_LABEL: Record<RegulatoryDocumentType, string> = {
  Certificate: 'Сертифікат',
  Declaration: 'Декларація',
  Registration: 'Реєстрація',
  License: 'Ліцензія',
  Permit: 'Дозвіл',
};

const MD_STATUS_OPTIONS: { value: RegulatoryDocumentStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Active', label: 'Активний' },
  { value: 'Expiring', label: 'Закінчується' },
  { value: 'Expired', label: 'Прострочений' },
];

export default function RegulatoryDocumentsPage() {
  const [mdSearch, setMdSearch] = useState('');
  const [mdTypeFilter, setMdTypeFilter] = useState<RegulatoryDocumentType | ''>('');
  const [mdStatusFilter, setMdStatusFilter] = useState<RegulatoryDocumentStatus | ''>('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdModalOpen, setMdModalOpen] = useState(false);
  const [mdEditingItem, setMdEditingItem] = useState<RegulatoryDocumentItem | null>(null);

  const [mdForm] = Form.useForm();

  const mdFilters: RegulatoryDocumentFilters = useMemo(() => ({
    search: mdSearch || undefined,
    documentType: mdTypeFilter || undefined,
    status: mdStatusFilter || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdTypeFilter, mdStatusFilter, mdPage, mdPageSize]);

  const { data: mdDocsData, isLoading: mdIsLoading } = useRegulatoryDocuments(mdFilters);
  const mdCreateMutation = useCreateRegulatoryDocument();
  const mdUpdateMutation = useUpdateRegulatoryDocument();
  const mdDeleteMutation = useDeleteRegulatoryDocument();

  const mdHandleOpenCreate = () => {
    setMdEditingItem(null);
    mdForm.resetFields();
    setMdModalOpen(true);
  };

  const mdHandleOpenEdit = (mdRecord: RegulatoryDocumentItem) => {
    setMdEditingItem(mdRecord);
    mdForm.setFieldsValue({
      productId: mdRecord.productId,
      documentType: mdRecord.documentType,
      documentNumber: mdRecord.documentNumber,
      issuedDate: mdRecord.issuedDate ? dayjs(mdRecord.issuedDate) : undefined,
      expiryDate: mdRecord.expiryDate ? dayjs(mdRecord.expiryDate) : undefined,
      notes: mdRecord.notes ?? undefined,
    });
    setMdModalOpen(true);
  };

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();
    const mdFields = {
      documentType: mdValues.documentType,
      documentNumber: mdValues.documentNumber,
      issuedDate: mdToUtcDate(mdValues.issuedDate),
      expiryDate: mdToUtcDate(mdValues.expiryDate),
      notes: mdValues.notes?.trim() || undefined,
    };

    if (mdEditingItem) {
      // PUT замінює всі поля: шлях до файлу форма не редагує — передаємо наявний, інакше сервер його очистить.
      // Виріб сервер при оновленні не змінює
      await mdUpdateMutation.mutateAsync({
        ...mdFields,
        id: mdEditingItem.id,
        filePath: mdEditingItem.filePath ?? undefined,
      });
    } else {
      await mdCreateMutation.mutateAsync({ ...mdFields, productId: mdValues.productId });
    }
    setMdModalOpen(false);
  };

  const mdGetRowClassName = (mdRecord: RegulatoryDocumentItem): string => {
    if (mdRecord.isExpired) return 'row-expired';
    if (mdIsExpiringSoon(mdRecord)) return 'row-expiring';
    return '';
  };

  const mdColumns: ColumnsType<RegulatoryDocumentItem> = [
    {
      title: 'Продукт',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Тип документа',
      dataIndex: 'documentType',
      key: 'documentType',
      width: 150,
      // На сервері тип — довільний рядок
      render: (mdValue: string) => MD_DOC_TYPE_LABEL[mdValue as RegulatoryDocumentType] ?? mdValue,
    },
    {
      title: 'Номер',
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      width: 180,
    },
    {
      title: 'Дата видачі',
      dataIndex: 'issuedDate',
      key: 'issuedDate',
      width: 120,
      render: (mdValue: string | null) => mdFormatDate(mdValue),
    },
    {
      title: 'Термін дії',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (mdValue: string | null) => mdFormatDate(mdValue),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 140,
      render: (_: unknown, mdRecord: RegulatoryDocumentItem) => {
        if (mdRecord.isExpired) return <Tag color="red">Прострочений</Tag>;
        if (mdIsExpiringSoon(mdRecord)) return <Tag color="orange">Закінчується</Tag>;
        return <Tag color="green">Активний</Tag>;
      },
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 160,
      render: (_: unknown, mdRecord: RegulatoryDocumentItem) => (
        <Space>
          <Button type="link" size="small" onClick={() => mdHandleOpenEdit(mdRecord)}>
            Редагувати
          </Button>
          <Popconfirm
            title="Видалити документ?"
            onConfirm={() => mdDeleteMutation.mutate(mdRecord.id)}
            okText="Так"
            cancelText="Ні"
          >
            <Button type="link" size="small" danger>
              Видалити
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .row-expired { background-color: #fff1f0 !important; }
        .row-expired:hover > td { background-color: #ffccc7 !important; }
        .row-expiring { background-color: #fffbe6 !important; }
        .row-expiring:hover > td { background-color: #fff1b8 !important; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Регуляторні документи
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
          Додати документ
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          options={MD_DOC_TYPE_OPTIONS}
          value={mdTypeFilter}
          onChange={(mdValue) => {
            setMdTypeFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
      </Space>

      <Table<RegulatoryDocumentItem>
        columns={mdColumns}
        dataSource={mdDocsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        rowClassName={mdGetRowClassName}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdDocsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <Modal
        title={mdEditingItem ? 'Редагувати документ' : 'Додати документ'}
        open={mdModalOpen}
        onOk={mdHandleSubmit}
        onCancel={() => setMdModalOpen(false)}
        confirmLoading={mdCreateMutation.isPending || mdUpdateMutation.isPending}
        okText={mdEditingItem ? 'Зберегти' : 'Додати'}
        cancelText="Скасувати"
        width={600}
      >
        <Form form={mdForm} layout="vertical">
          <Form.Item
            name="productId"
            label="Продукт (ID)"
            rules={[{ required: true, message: 'Введіть ID продукту' }]}
          >
            <Input disabled={!!mdEditingItem} />
          </Form.Item>
          <Form.Item
            name="documentType"
            label="Тип документа"
            rules={[{ required: true, message: 'Оберіть тип документа' }]}
          >
            <Select
              options={MD_DOC_TYPE_OPTIONS.filter((mdOpt) => mdOpt.value !== '')}
            />
          </Form.Item>
          <Form.Item
            name="documentNumber"
            label="Номер документа"
            rules={[{ required: true, message: 'Введіть номер документа' }]}
          >
            <Input />
          </Form.Item>
          {/* Дати на сервері необов'язкові */}
          <Form.Item name="issuedDate" label="Дата видачі">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="expiryDate"
            label="Термін дії до"
            dependencies={['issuedDate']}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_: unknown, mdValue: Dayjs | undefined) => {
                  const mdIssued: Dayjs | undefined = getFieldValue('issuedDate');
                  // Сервер відхиляє термін дії, що не пізніше дати видачі
                  return !mdValue || !mdIssued || mdValue.isAfter(mdIssued, 'day')
                    ? Promise.resolve()
                    : Promise.reject(new Error('Термін дії має бути пізніше дати видачі'));
                },
              }),
            ]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Примітки"
            rules={[{ max: 2000, message: 'Не більше 2000 символів' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
